import { TilesError } from '@/plugins/tiles/TilesError'
import type { TilesAdaptiveBudgetOptions, TilesBudgetPressure } from '@/plugins/tiles/types'

/** 纯时间/容量决策，不执行淘汰、不清除运行时回退或失败记录。 */
export class TileAdaptiveBudgetController {
  readonly options: Readonly<Required<TilesAdaptiveBudgetOptions>>
  private _pressureSince?: number
  private _lowSince?: number
  private _lastChange = -Infinity

  constructor(options: TilesAdaptiveBudgetOptions, currentBytes: number) {
    const resolved = {
      stepBytes: 32 * 1024 * 1024,
      checkIntervalMs: 1000,
      pressureDelayMs: 3000,
      cooldownMs: 10000,
      shrinkDelayMs: 60000,
      ...options,
    }
    for (const key of [
      'minCacheBytes',
      'maxCacheBytes',
      'stepBytes',
      'checkIntervalMs',
      'pressureDelayMs',
      'cooldownMs',
      'shrinkDelayMs',
    ] as const) {
      const value = resolved[key]
      if (!Number.isSafeInteger(value) || value <= 0)
        throw new TilesError({
          code: 'invalid-input',
          stage: 'runtime',
          message: `Tiles 自动预算配置 ${key} 必须为安全正整数`,
        })
    }
    if (
      resolved.minCacheBytes > resolved.maxCacheBytes ||
      currentBytes < resolved.minCacheBytes ||
      currentBytes > resolved.maxCacheBytes ||
      resolved.checkIntervalMs < 100 ||
      resolved.checkIntervalMs > 2_147_483_647 ||
      resolved.pressureDelayMs < resolved.checkIntervalMs ||
      resolved.shrinkDelayMs < resolved.cooldownMs ||
      resolved.shrinkDelayMs < resolved.pressureDelayMs
    )
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: 'Tiles 自动预算区间或计时间隔无效，当前额度须在上下限内',
      })
    this.options = Object.freeze(resolved)
  }

  /** 暂停、需求切换或手动配置后重新累计，保留调整冷却。 */
  resetObservation(): void {
    this._pressureSince = undefined
    this._lowSince = undefined
  }

  evaluate(
    now: number,
    currentBytes: number,
    protectedAndReservedBytes: number,
    pressure: TilesBudgetPressure,
    busy: boolean
  ): number | undefined {
    const options = this.options
    const requiredLimit = protectedAndReservedBytes + pressure.requiredBytes
    const canGrow =
      pressure.requiredBytes > 0 &&
      pressure.reason === 'local' &&
      requiredLimit <= options.maxCacheBytes &&
      currentBytes < options.maxCacheBytes
    const shrinkFloor = Math.max(
      options.minCacheBytes,
      protectedAndReservedBytes + options.stepBytes
    )
    const canShrink =
      !busy &&
      pressure.requiredBytes === 0 &&
      pressure.reason === 'none' &&
      currentBytes - options.stepBytes >= shrinkFloor
    this._pressureSince = canGrow ? (this._pressureSince ?? now) : undefined
    this._lowSince = canShrink ? (this._lowSince ?? now) : undefined
    if (now - this._lastChange < options.cooldownMs) return
    let next: number | undefined
    if (this._pressureSince !== undefined && now - this._pressureSince >= options.pressureDelayMs)
      next = Math.min(options.maxCacheBytes, currentBytes + options.stepBytes)
    else if (this._lowSince !== undefined && now - this._lowSince >= options.shrinkDelayMs)
      next = Math.max(shrinkFloor, currentBytes - options.stepBytes)
    if (next === undefined || next === currentBytes) return
    this._lastChange = now
    this.resetObservation()
    return next
  }
}
